import { EditorContent } from "@tiptap/react";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import { EditorTypography } from "UI/FnrTextEditor/EditorTypography";
import MenuBar from "UI/FnrTextEditor/MenuBar";
/**
 * This variant only shows the save button UI when the editor is focused.
 */
export default function TextEditorHoverMenuVariant({
  textInternal = "",
  setTextInternal = () => {},
  lineHeight = "1.2rem",
  minHeight,
  maxHeight,
  autofocus = "end",
  menuButtons = ["bold", "italic", "ul", "ol", "link", "unlink"],
  renderSaveButton,
  characterLimit,
  placeholder = "",
  className = "",
  menuStyles = {},
  editorDataCy = "",
  editorRef,
  editor,
}) {
  const [showMenuBar, setShowMenuBar] = useState(false);



  const focusOnEditor = useCallback(() => {
    editor?.view?.dom.focus();
  }, [editor?.view?.dom]);

  return (
    <>
      <Wrapper
        ref={editorRef}
        className={className}
        onClick={focusOnEditor}
        onBlur={() => {
          // timeout pushes these actions to the end of the event loop
          // so that the click event on the editor is triggered first
          // and the editor is actually blurred
          setTimeout(() => {
            editor?.view?.dom.blur();
            setShowMenuBar(false);
          }, 250);
        }}
        onFocus={() => setShowMenuBar(true)}
        $focused={showMenuBar}
        data-cy="Text_Editor_Hover_Menu_Variant_Wrapper"
      >
        <EditorWrapper
          minHeight={minHeight}
          maxHeight={maxHeight}
          lineHeight={lineHeight}
        >
          <EditorContent data-cy={editorDataCy} editor={editor} />
          {renderSaveButton && renderSaveButton(showMenuBar)}
        </EditorWrapper>

        <AnimatePresence>
          <MenuWrapper $focused={showMenuBar}>
            <MenuBar
              menuButtons={menuButtons}
              editor={editor}
              style={menuStyles}
            />
          </MenuWrapper>
        </AnimatePresence>
      </Wrapper>
    </>
  );
}

TextEditorHoverMenuVariant.propTypes = {
  textInternal: PropTypes.string,
  setTextInternal: PropTypes.func,
  lineHeight: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autofocus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  menuButtons: PropTypes.arrayOf(PropTypes.string),
  renderSaveButton: PropTypes.func,
  characterLimit: PropTypes.number,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  menuStyles: PropTypes.object,
  editorDataCy: PropTypes.string,
  editorRef: PropTypes.object,
  editor: PropTypes.object,
};

const Wrapper = styled(motion.div)`
  display: flex;
  position: relative;
  border-radius: 12px;
  padding: 14px 8px 10px 8px;
  background-color: hsl(0 0% 100% / 0.12);
  transition: border 250ms;
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: background-color 250ms;

  &:hover:not(:focus-within) {
    background-color: hsl(0 0% 100% / 0.25);
  }

  &:focus-within {
    outline: none;
  }

  & .ProseMirror p.is-editor-empty:first-child::before {
    color: hsl(195 12% 93% / 0.75);
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
`;

const MenuWrapper = styled.div`
  margin-bottom: 5px;
  position: absolute;
  top: -20px;
  right: 8px;
  opacity: ${(p) => (p.$focused ? "1" : "0")};
  scale: ${(p) => (p.$focused ? "1" : "0.9")};
  transition: opacity 300ms, scale 300ms;
  pointer-events: ${(p) => (p.$focused ? "all" : "none")};
`;

const EditorWrapper = styled(EditorTypography)`
  flex: 1;
  max-width: 100%;

  .ProseMirror {
    padding: 4px 3px;
    outline: none;
    border-radius: 10px;
    font-weight: 400;
    font-size: 18px;

    max-height: ${({ maxHeight }) => (maxHeight ? maxHeight : "none")};
    min-height: ${({ minHeight }) => (minHeight ? minHeight : "none")};
    overflow-y: auto;
    > * {
      line-height: ${(props) => props.lineHeight};
    }
    & p {
      margin: 0;
    }
  }
`;